import { useCallback, useEffect, useRef, useState } from "react";

import {
  getBrandEligibility,
  markBrandApplied,
} from "../../services/apiV2";
import { TBrandEligibilityResponse } from "../../services/apiV2.types";

/** While the first scan runs, results are refreshed this often. */
export const SCAN_POLL_MS = 30_000;

export const useBrandUngating = () => {
  const [data, setData] = useState<TBrandEligibilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [applyingIds, setApplyingIds] = useState<string[]>([]);
  const mounted = useRef(true);

  const load = useCallback(async (showLoading: boolean) => {
    if (showLoading) setLoading(true);
    try {
      const response = await getBrandEligibility();
      if (!mounted.current) return;
      setData(response.data);
      setError(false);
    } catch {
      if (mounted.current && showLoading) setError(true);
    } finally {
      if (mounted.current && showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load(true);
    return () => {
      mounted.current = false;
    };
  }, [load]);

  // Keep refreshing quietly while the first scan is still running.
  useEffect(() => {
    if (data?.scanState !== "scanning") return;
    const timer = window.setInterval(() => load(false), SCAN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [data?.scanState, load]);

  useEffect(() => {
    if (
      data?.scanState !== "not_connected" && 
      data?.scanState !== "reconnect_required"
    )
      return;

    const onFocus = () => {
      if (document.visibilityState === "visible") load(false);
    }

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilityChange", onFocus);
    }
  }, [data?.scanState, load]);

  const markApplied = useCallback(async (brandId: string) => {
    setApplyingIds((ids) => [...ids, brandId]);
    try {
      await markBrandApplied(brandId);
      if (!mounted.current) return;
      const now = new Date().toISOString();
      setData((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.brandId === brandId ? { ...item, appliedAt: now } : item,
              ),
            }
          : current,
      );
      return true;
    } catch {
      return false;
    } finally {
      if (mounted.current)
        setApplyingIds((ids) => ids.filter((id) => id !== brandId));
    }
  }, []);

  return {
    data,
    loading,
    error,
    applyingIds,
    reload: () => load(true),
    /** Reloads without the full-page spinner (keeps the ASIN check panel and its results on screen). */
    refresh: () => load(false),
    markApplied,
  };
};
